<template>
  <layout-basic>
    <page-hero
      title="IMPROVE vs Citation Learning"
      lead="Service-led learning offerings—or a simple, SME-first platform you can run yourself."
      eyebrow="Compare"
    />

    <section class="demo-video-section">
      <v-container style="max-width: 960px">
        <demo-video video-key="compare" />
      </v-container>
    </section>

    <section class="vs-intro">
      <v-container style="max-width: 740px">
        <p>
          Citation Learning often sits alongside broader HR, health &amp; safety or
          employment support services. That can suit organisations buying advice and
          learning together. IMPROVE is a dedicated learning platform for growing
          businesses: upload your own content, access 130+ courses, and cover
          onboarding, compliance and development with transparent annual pricing.
        </p>
      </v-container>
    </section>

    <section class="vs-table-section">
      <v-container style="max-width: 900px">
        <h2 class="font-sora section-heading">At a glance</h2>
        <div class="vs-table" role="table">
          <div class="vs-table__head" role="row">
            <div role="columnheader">Area</div>
            <div role="columnheader">Typical service-led learning</div>
            <div role="columnheader">IMPROVE</div>
          </div>
          <div
            v-for="(row, i) in rows"
            :key="i"
            class="vs-table__row"
            role="row"
          >
            <div class="vs-table__label" role="cell">{{ row.label }}</div>
            <div role="cell">{{ row.theirs }}</div>
            <div class="vs-table__improve" role="cell">{{ row.ours }}</div>
          </div>
        </div>
        <p class="vs-disclaimer">
          High-level category comparison only—not a claim about any specific
          third-party pricing or feature list. Always check current offerings
          directly.
        </p>
      </v-container>
    </section>

    <section class="vs-challenge">
      <v-container style="max-width: 900px">
        <h2 class="font-sora section-heading">Challenge vs IMPROVE</h2>
        <div class="challenge-grid">
          <div
            v-for="(item, i) in challenges"
            :key="i"
            class="challenge-card"
          >
            <div class="challenge-card__side">
              <span class="challenge-card__label">Challenge</span>
              <p>{{ item.challenge }}</p>
            </div>
            <div class="challenge-card__side challenge-card__side--improve">
              <span class="challenge-card__label">IMPROVE</span>
              <p>{{ item.improve }}</p>
            </div>
          </div>
        </div>
      </v-container>
    </section>

    <point-grid
      title="Why switch to IMPROVE"
      :points="whyPoints"
      :columns="2"
      tint
    />

    <section class="vs-links">
      <v-container style="max-width: 720px">
        <p>
          <router-link to="/compare">← All comparisons</router-link>
          ·
          <router-link to="/why-switch">Why switch</router-link>
          ·
          <router-link to="/compare/breathe-learn">vs Breathe</router-link>
          ·
          <router-link to="/compare/brighthr">vs BrightHR</router-link>
          ·
          <router-link to="/compare/ihasco">vs iHASCO</router-link>
        </p>
      </v-container>
    </section>

    <standard-cta
      title="Compare before you decide."
      lead="Watch the demo or experience IMPROVE for yourself."
    />
  </layout-basic>
</template>

<script>
import LayoutBasic from '@/layouts/Dashboard';
import PageHero from '@/components/marketing/PageHero.vue';
import PointGrid from '@/components/marketing/PointGrid.vue';
import StandardCta from '@/components/marketing/StandardCta.vue';
import DemoVideo from '@/components/DemoVideo.vue';
import { marketingPageMeta } from '@/utils/marketingPage';

export default {
  name: 'CompareCitation',
  components: {
    LayoutBasic,
    PageHero,
    PointGrid,
    StandardCta,
    DemoVideo,
  },
  metaInfo() {
    return marketingPageMeta({
      title: 'IMPROVE vs Citation Learning | IMPROVE',
      description:
        'Compare IMPROVE with Citation Learning: SME-first LMS vs typical service-led learning offerings.',
      path: '/compare/citation',
    });
  },
  data() {
    return {
      rows: [
        {
          label: 'Primary focus',
          theirs: 'Learning alongside advisory / HR services',
          ours: 'Dedicated LMS for day-to-day training',
        },
        {
          label: 'Built for',
          theirs: 'Organisations buying services + learning',
          ours: 'SMEs (typically 5–250 people)',
        },
        {
          label: 'Your content',
          theirs: 'Varies by package and support model',
          ours: 'Upload your own courses & documents',
        },
        {
          label: 'Ready-made library',
          theirs: 'Learning content within the offering',
          ours: '130+ courses included',
        },
        {
          label: 'Pricing model',
          theirs: 'Often bundled with services',
          ours: 'Simple annual price per business',
        },
        {
          label: 'Leadership development',
          theirs: 'Depends on wider package',
          ours: 'Leadership Plus bolt-on available',
        },
      ],
      challenges: [
        {
          challenge: 'Learning is wrapped into a wider services relationship.',
          improve: 'A clear platform you can run without buying a full services suite.',
        },
        {
          challenge: 'You want control over your own policies and induction content.',
          improve: 'Upload your own content plus 130+ ready-made courses.',
        },
        {
          challenge: 'Bundled pricing makes the learning cost hard to isolate.',
          improve: 'Transparent annual pricing designed for growing businesses.',
        },
      ],
      whyPoints: [
        'Easier to use',
        'Upload your own courses',
        'Better value for SMEs',
        'Everything in one platform',
      ],
    };
  },
};
</script>

<style scoped>
.vs-intro {
  padding: 8px 0 16px;
  text-align: center;
}

.vs-intro p {
  color: var(--brand-grey, #6b7280);
  font-size: 16px;
  line-height: 1.6;
  margin: 0;
}

.section-heading {
  text-align: center;
  color: var(--brand-navy, #002458);
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 24px;
}

.vs-table-section {
  padding: 16px 0 32px;
}

.vs-table {
  border: 1px solid #e6ebf1;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
}

.vs-table__head,
.vs-table__row {
  display: grid;
  grid-template-columns: 1.1fr 1.4fr 1.4fr;
}

.vs-table__head {
  background: var(--brand-navy, #002458);
  color: #fff;
  font-weight: 700;
  font-size: 14px;
}

.vs-table__head > div,
.vs-table__row > div {
  padding: 14px 16px;
  border-bottom: 1px solid #e6ebf1;
  font-size: 14px;
  line-height: 1.45;
}

.vs-table__row:last-child > div {
  border-bottom: none;
}

.vs-table__label {
  font-weight: 700;
  color: var(--brand-navy, #002458);
  background: #f7f9fb;
}

.vs-table__improve {
  color: var(--brand-navy, #002458);
  font-weight: 600;
  background: rgba(8, 160, 168, 0.06);
}

.vs-disclaimer {
  margin: 12px 0 0;
  font-size: 12px;
  color: var(--brand-grey, #6b7280);
  text-align: center;
  line-height: 1.45;
}

.vs-challenge {
  padding: 24px 0 40px;
  background: #f7f9fb;
}

.challenge-grid {
  display: grid;
  gap: 14px;
}

.challenge-card {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 12px;
  padding: 16px;
}

.challenge-card__label {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--brand-teal, #08a0a8);
  margin-bottom: 6px;
}

.challenge-card__side p {
  margin: 0;
  color: var(--brand-navy, #002458);
  font-size: 15px;
  line-height: 1.45;
  font-weight: 600;
}

.challenge-card__side--improve {
  border-left: 1px solid #e6ebf1;
  padding-left: 12px;
}

.vs-links {
  padding: 24px 0 8px;
  text-align: center;
}

.vs-links p {
  margin: 0;
  color: var(--brand-grey, #6b7280);
  font-size: 14px;
  line-height: 1.8;
}

.vs-links a {
  color: var(--brand-teal, #08a0a8);
  font-weight: 700;
  text-decoration: none;
}

.vs-links a:hover {
  text-decoration: underline;
}

@media screen and (max-width: 800px) {
  .vs-table__head,
  .vs-table__row {
    grid-template-columns: 1fr;
  }

  .vs-table__head > div:first-child {
    display: none;
  }

  .challenge-card {
    grid-template-columns: 1fr;
  }

  .challenge-card__side--improve {
    border-left: none;
    border-top: 1px solid #e6ebf1;
    padding-left: 0;
    padding-top: 12px;
  }

  .section-heading {
    font-size: 24px;
  }
}
</style>
